import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	CommunityInFeed,
	CurrentCommunityInfo,
	FeedPageModel,
	getFeedPageInfo
} from "client/Api/Communities/communityFeed";
import {
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useStableImperativeCallback,
	useToggle,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	BusyIndicator,
	ContextActionBar,
	NoResults,
	PageContentTitle,
	ToolbarAction,
	useConfirmationDialog
} from "client/Components";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./CommunityFeedPage.module.less";
import { never } from "client_legacy/ndaw/errors";
import {
	CommunityKind,
	deleteCommunity,
	joinCommunity,
	leaveCommunity,
	markCommunityAsSeen,
	registerFollowCommunity,
	unregisterFollowCommunity
} from "client/Api/Communities/community";
import { NavigationCallback } from "client/Navigation";
import {
	CommunityEvent,
	publish,
	subscribe,
	unsubscribe
} from "./events";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { getHubNavigationForCommunity } from "./CommunitiesPage";
import { HubDataSource } from "client/Api/Hub/hub";
import classNames from "classnames";
import { CommunityFeed } from "./CommunityFeed";
import { CommunityInfo } from "./CommunityInfo";
import { SaveCommunityResult, useEditCommunityDialog } from "./EditCommunityDialog/EditCommunityDialog";
import { useCommunityDeletionConfirmationDialog } from "./CommunityDeletionConfirmationDialog";
import { CommunityNodeId } from "../Sidebar/HubSidebar";
import { COMMUNITY_POST_COMMENT_SECTION_FRAGMENT } from "./CommunityPostPage";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { HubEvent, publish as publishHubEvent } from "../events";

export type CommunityFeedPageProps = {
	id: CommunityId;
	onNavigate: NavigationCallback;
};

export const CommunityFeedPage = (props: CommunityFeedPageProps): ReactElement => {
	const {
		id,
		onNavigate
	} = props;

	const [feedPageInfo, setFeedPageInfo] = useState<FeedPageInfoLoadingState>({ kind: "loading" });
	const [isChangingMembership, setIsChangingMembership] = useState<boolean>(false);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [onReloadFeedPageInfo, feedPageInfoVersion] = useForceRerender();
	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const [hasScroll, toggleHasScroll] = useToggle(true);
	const pageRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [editDialog, openEditDialog] = useEditCommunityDialog();
	const [deleteCommunityConfirmationDialog, openDeleteCommunityConfirmationDialog] = useCommunityDeletionConfirmationDialog();

	const clearCommunityNewStatusHandler = useCallback(
		async (communityId: number, signal: AbortSignal): Promise<void> => {
			try {
				await markCommunityAsSeen(
					communityId,
					signal
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}

			publishHubEvent(
				HubEvent.IsNewStatusCleared,
				{
					kind: HubDataSource.Community,
					communityId
				}
			);
		},
		[]
	);

	const stableClearCommunityNewStatusHandler: typeof clearCommunityNewStatusHandler = useStableImperativeCallback(clearCommunityNewStatusHandler);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(feedPageInfoVersion);

			const load = async (signal: AbortSignal): Promise<void> => {
				setFeedPageInfo({ kind: "loading"});

				try {
					const {
						communitiesInFeed,
						currentCommunity,
						options
					} = await getFeedPageInfo(id, signal);

					if (isNotNil(currentCommunity)) {
						stableClearCommunityNewStatusHandler(currentCommunity.id, signal);
					}

					setFeedPageInfo({
						kind: "loaded",
						communitiesInFeed,
						options,
						currentCommunity
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					setFeedPageInfo({ kind: "error" });

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			id,
			alertManager,
			feedPageInfoVersion,
			stableClearCommunityNewStatusHandler
		]
	);

	const currentCommunity = useMemo(
		(): CommunityInFeedWithMyFeed | undefined => {
			if (feedPageInfo.kind !== "loaded") {
				return undefined;
			}

			if (isNil(feedPageInfo.currentCommunity)) {
				return {
					id: "my-feed",
					name: localize("_MyFeed")
				};
			}

			return feedPageInfo.currentCommunity;
		},
		[feedPageInfo]
	);

	const updateCommunityInLoadedFeedData = useCallback(
		(
			id: number,
			updateCallback: (community: CommunityInFeed) => CommunityInFeed
		): void => {
			setFeedPageInfo(
				(x: FeedPageInfoLoadingState): FeedPageInfoLoadingState => {
					if (x.kind !== "loaded") {
						return x;
					}

					const { communitiesInFeed } = x;

					return {
						...x,
						communitiesInFeed: communitiesInFeed.map(
							(x): CommunityInFeed => {
								if (x.id !== id) {
									return x;
								}

								return updateCallback(x);
							}
						)
					};
				}
			);
		},
		[]
	);

	const changeIsMemberHandler = useCallback(
		(isMember: boolean): void => {
			setFeedPageInfo(
				x => {
					if (x.kind !== "loaded"
						|| !x.currentCommunity
					) {
						return x;
					}

					const {
						currentCommunity
					} = x;

					const {
						membersCount,
						userInfo
					} = currentCommunity;

					return {
						...x,
						currentCommunity: {
							...currentCommunity,
							userInfo: {
								...userInfo,
								isMember
							},
							membersCount: isMember
								? membersCount + 1
								: membersCount - 1
						}
					};
				}
			);
		},
		[]
	);

	const joinHandler = useCallback(
		async (): Promise<void> => {
			if (typeof currentCommunity?.id !== "number") {
				throw new Error(`Cannot join community with id: ${currentCommunity?.id}`);
			}

			setIsChangingMembership(true);

			try {
				await joinCommunity(currentCommunity.id, globalCancellation);
				changeIsMemberHandler(true);

				publish(
					CommunityEvent.JoinedCommunity,
					{
						id: currentCommunity.id,
						name: currentCommunity.name
					}
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsChangingMembership(false);
		},
		[
			alertManager,
			currentCommunity,
			globalCancellation,
			changeIsMemberHandler
		]
	);

	const followCommunity = useCallback(
		async (): Promise<void> => {
			if (typeof currentCommunity?.id !== "number") {
				throw new Error(`Cannot join community with id: ${currentCommunity?.id}`);
			}

			try {
				await registerFollowCommunity(currentCommunity.id, globalCancellation);

				updateCommunityInLoadedFeedData(
					currentCommunity.id,
					(x: CommunityInFeed): CommunityInFeed => ({
						...x,
						isFollowing: true
					})
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			currentCommunity,
			globalCancellation,
			updateCommunityInLoadedFeedData
		]
	);

	const unfollowCommunity = useCallback(
		async (): Promise<void> => {
			if (typeof currentCommunity?.id !== "number") {
				throw new Error(`Cannot join community with id: ${currentCommunity?.id}`);
			}

			try {
				await unregisterFollowCommunity(currentCommunity.id, globalCancellation);

				updateCommunityInLoadedFeedData(
					currentCommunity.id,
					(x: CommunityInFeed): CommunityInFeed => ({
						...x,
						isFollowing: false
					})
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			currentCommunity,
			globalCancellation,
			updateCommunityInLoadedFeedData
		]
	);

	const leaveHandler = useCallback(
		async (): Promise<void> => {
			if (typeof currentCommunity?.id !== "number") {
				throw new Error(`Cannot leave community with id: ${currentCommunity?.id}`);
			}

			const result: TryResult<void> = await openConfirmationDialog({
				trackingId: "leave-community-confirmation-dialog",
				title: localize("_AreYouSureYouWantToLeaveThisCommunity"),
				children: localize("_YouCanBecomeAMemberAgainViaTheDiscoverCommunitiesPage"),
				confirmText: localize("_Leave")
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsChangingMembership(true);

			try {
				await leaveCommunity(currentCommunity.id, globalCancellation);
				changeIsMemberHandler(false);

				publish(
					CommunityEvent.LeftCommunity,
					{
						communityId: currentCommunity.id
					}
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsChangingMembership(false);
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog,
			currentCommunity?.id,
			changeIsMemberHandler
		]
	);

	const {
		canDelete,
		canEdit
 	} = useMemo(
		(): {
			canEdit: boolean;
			canDelete: boolean;
		} => {
			if (typeof currentCommunity?.id !== "number") {
				return {
					canEdit: false,
					canDelete: false
				};
			}

			const {
				canEdit,
				canDelete
			} = currentCommunity.userInfo;

			return {
				canDelete,
				canEdit
			};
		},
		[currentCommunity]
	);

	const isMember = useMemo(
		(): boolean => {
			return typeof currentCommunity?.id === "number"
				&& currentCommunity
					.userInfo
					.isMember;
		},
		[currentCommunity]
	);

	const isFollowing = useMemo(
		(): boolean => {
			if (feedPageInfo.kind !== "loaded") {
				return false;
			}

			const currentCommunity: CommunityInFeed | undefined = feedPageInfo
				.communitiesInFeed
				.find(x => x.id === id);

			return typeof currentCommunity?.id === "number"
				&& currentCommunity.isFollowing;
		},
		[feedPageInfo, id]
	);

	useEffect(
		(): (() => void) => {
			const leftToken: string = subscribe(
				CommunityEvent.LeftCommunity,
				(_, data) => {
					updateCommunityInLoadedFeedData(
						data.communityId,
						(x: CommunityInFeed): CommunityInFeed => ({
							...x,
							isMember: false
						})
					);
				}
			);

			const joinToken: string = subscribe(
				CommunityEvent.JoinedCommunity,
				(_, data) => {
					updateCommunityInLoadedFeedData(
						data.id,
						(x: CommunityInFeed): CommunityInFeed => ({
							...x,
							isMember: true,
							isFollowing: true
						})
					);
				}
			);

			return (): void => {
				unsubscribe(leftToken);
				unsubscribe(joinToken);
			};
		},
		[updateCommunityInLoadedFeedData]
	);

	const isCustomCommunity = useMemo(
		(): boolean => {
			return typeof currentCommunity?.id === "number"
				&& currentCommunity.kind === CommunityKind.Custom;
		},
		[currentCommunity]
	);

	const editHandler = useCallback(
		async(): Promise<void> => {
			if (typeof id !== "number") {
				throw new Error("Can not edit a community without a valid id");
			}

			const result: TryResult<SaveCommunityResult> = await openEditDialog({
				communityId: id
			});

			if (!result.wasSuccessful) {
				return;
			}

			onReloadFeedPageInfo();
		},
		[
			id,
			onReloadFeedPageInfo,
			openEditDialog
		]
	);

	const navigateToCommunityHandler = useCallback(
		(communityId: CommunityNodeId): void => {
			onNavigate(
				getHubNavigationForCommunity({
					source: HubDataSource.Community,
					communityId
				})
			);
		},
		[onNavigate]
	);

	const deleteHandler = useCallback(
		async (): Promise<void> => {
			if (typeof id !== "number") {
				throw new Error("Can not delete a community without a valid id");
			}

			const result: TryResult<void> = await openDeleteCommunityConfirmationDialog();

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteCommunity(id, globalCancellation);
				alertManager.showSuccess(localize("_CommunitySuccessfullyDeleted"));
				publish(CommunityEvent.CommunityDeleted, { communityId: id });
				navigateToCommunityHandler("my-feed");
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsBusy(false);
		},
		[
			alertManager,
			globalCancellation,
			openDeleteCommunityConfirmationDialog,
			navigateToCommunityHandler,
			id
		]
	);

	const membershipActions = useMemo(
		(): readonly FeedPageAction[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "leave",
					title: (c) => c.isChangingMembership
						? localize("_Leaving") + "…"
						: localize("_Joined"),
					icon: "check",
					canExecute: (c) => c.isCustomCommunity && c.isMember,
					execute: () => leaveHandler()
				},
				{
					kind: ActionKind.Regular,
					name: "join",
					title: (c) => c.isChangingMembership
						? localize("_Joining") + "…"
						: localize("_Join"),
					icon: "plus",
					canExecute: (c) => c.isCustomCommunity && !c.isMember,
					execute: () => joinHandler()
				}
			];
		},
		[joinHandler, leaveHandler]
	);

	const moreActions = useMemo(
		(): readonly FeedPageAction[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "follow",
					title: localize("_Follow"),
					icon: "plus",
					canExecute: (x): boolean => x.isMember && !x.isFollowing,
					execute: () => followCommunity()
				},
				{
					kind: ActionKind.Regular,
					name: "unfollow",
					title: localize("_Following"),
					icon: "check",
					canExecute: (x): boolean => x.isMember && x.isFollowing,
					execute: () => unfollowCommunity()
				},
				{
					kind: ActionKind.Regular,
					name: "edit",
					title: localize("_Edit"),
					icon: "pen",
					canExecute: (x) => x.canEdit,
					execute: () => editHandler()
				},
				{
					kind: ActionKind.Regular,
					name: "delete",
					title: localize("_Delete"),
					icon: "trash-can",
					canExecute: (x) => x.canDelete,
					execute: () => deleteHandler()
				}
			];
		},
		[
			followCommunity,
			unfollowCommunity,
			deleteHandler,
			editHandler
		]
	);

	const topBarActions = useMemo(
		(): readonly FeedPageAction[] => {
			return buildActionsForTopBar<FeedPageActionContext, FeedPageAction>(
				{
					defaultActions: [
						...membershipActions,
						...moreActions
					]
				},
				false,
				isResponsive,
				false
			);
		},
		[
			membershipActions,
			moreActions,
			isResponsive
		]
	);

	const scrollToTopHandler = useCallback(
		(): void => {
			pageRef.current
				?.scrollTo({
					top: 0
				});
		},
		[]
	);

	const actionContext = useMemo(
		(): FeedPageActionContext => {
			return {
				isChangingMembership,
				isMember,
				isCustomCommunity,
				isFollowing,
				canEdit,
				canDelete
			};
		},
		[
			isChangingMembership,
			isMember,
			isCustomCommunity,
			isFollowing,
			canDelete,
			canEdit
		]
	);

	const openPostHandler = useCallback(
		(postId: number, deeplink?: "comments"): void => {
			onNavigate(
				{
					...getHubNavigationForCommunity({
						source: HubDataSource.Community,
						postId,
						communityId: id
					}),
					fragment: deeplink === "comments"
						? COMMUNITY_POST_COMMENT_SECTION_FRAGMENT
						: undefined
				}
			);
		},
		[onNavigate, id]
	);

	const renderContent = (): ReactElement => {
		switch (feedPageInfo.kind) {
			case "loading":
				return <BusyIndicator />;
			case "error":
				return (
					<div className={styles.noResultContainer}>
						<NoResults
							icon="gear"
							title={localize("_NotAbleToRetrievePosts")}
							message={localize("_WeWereUnableToRetrieveYourPosts")}
							height="fill"
							onRetry={onReloadFeedPageInfo}
						/>
					</div>
				);
			case "loaded":
				return (
					<>
						{isNotNil(currentCommunity) && (
							<div className={styles.communityInfo}>
								{currentCommunity.id === "my-feed"
									? (
										<PageContentTitle
											title={currentCommunity.name}
											actionContext={actionContext}
											actions={topBarActions}
										/>
									)
									: (
										<CommunityInfo
											model={currentCommunity}
											membershipActions={membershipActions}
											moreActions={moreActions}
											actionContext={actionContext}
											isMobile={isMobile}
										/>
									)
								}
							</div>
						)}

						{isNotNil(currentCommunity)
							&& currentCommunity.id !== "my-feed"
							&& (
								<h3 className={styles.sectionTitle}>
									{localize("_Posts")}
								</h3>
							)
						}

						<CommunityFeed
							id={id}
							communitiesInFeed={feedPageInfo.communitiesInFeed}
							options={feedPageInfo.options}
							onNavigateToCommunity={navigateToCommunityHandler}
							onOpenPost={openPostHandler}
							onScrollBehaviorChange={toggleHasScroll}
							onNewPost={scrollToTopHandler}
						/>
						<AiAssistantFabSpacing />
					</>
				);
			default:
				never(feedPageInfo);
		}
	};

	return (
		<>
			<div
				{...setTrackingId("community-feed-page")}
				className={styles.root}
			>
				{isBusy && <BusyIndicator/>}
				{isMobile && (
					<ContextActionBar
						actions={topBarActions}
						context={actionContext}
						hasBorderBottom
						trackingId="global-action-bar"
					/>
				)}
				<div
					ref={pageRef}
					className={classNames(
						styles.page,
						{
							[styles.page_noScroll]: !hasScroll
						}
					)}
				>
					<div className={styles.content}>
						{renderContent()}
					</div>
				</div>
			</div>
			{confirmationDialog}
			{editDialog}
			{deleteCommunityConfirmationDialog}
		</>
	);
};
CommunityFeedPage.displayName="CommunityFeedPage";

export type CommunityId = "my-feed" | number;

type FeedPageInfoLoadingState = {
	kind: "error" | "loading";
} | (
	{
		kind: "loaded";
	} & FeedPageModel
);

type CommunityInFeedWithMyFeed = CurrentCommunityInfo
	| {
		id: "my-feed";
		name: string;
	};

export type FeedPageActionContext = {
	isChangingMembership: boolean;
	isMember: boolean;
	isCustomCommunity: boolean;
	isFollowing: boolean;
	canEdit: boolean;
	canDelete: boolean;
};

export type FeedPageAction = ToolbarAction<FeedPageActionContext>;
